import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { Checkbox, BaseButton, HorizonLine, useT } from '@repo/common';
import SearchInFilterModal from './SearchInFilterModal';

import { ReactComponent as CloseIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';

import { getQnaCategories } from '../../../api/qna/qnaCategory.api';
import { getSearchQna } from '../../../api/qna/qnaSearch.api';

const MobileFilterBottomSheet = ({
  keyword,
  isOpen,
  onClose,
  filterState,
  setFilterState,
  setActiveCategory,
  appliedFilterState,
  onApply,
}) => {
  const t = useT();
  const [localFilter, setLocalFilter] = useState(filterState);
  const [categories, setCategories] = useState([]);
  const [checkedCategories, setCheckedCategories] = useState({});
  const [isFilterDirty, setIsFilterDirty] = useState(false);
  const bottomSheetRef = useRef(null);
  const [isSearchFilterModalOpen, setIsSearchFilterModalOpen] = useState(false);

  const DEFAULT_HEIGHT = 770;
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [sheetHeight, setSheetHeight] = useState(DEFAULT_HEIGHT);

  useEffect(() => {
    if (isOpen) {
      setSheetHeight(DEFAULT_HEIGHT);
      setCurrentY(0);
    }
  }, [isOpen]);

  const handleTouchStart = (e) => {
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    const touchY = e.touches[0].clientY;
    setCurrentY(touchY);

    const delta = startY - touchY;
    let newHeight = sheetHeight + delta;

    const maxHeight = window.innerHeight - 20;
    if (newHeight > maxHeight) newHeight = maxHeight;
    if (newHeight < 100) newHeight = 100;

    setSheetHeight(newHeight);
    setStartY(touchY);
  };

  const handleTouchEnd = () => {
    if (currentY > startY + 5 || sheetHeight < 500) {
      handleClose();
    } else {
      setStartY(0);
    }
  };

  const handleClose = () => {
    setSheetHeight(0);

    setTimeout(() => {
      onClose();
      setSheetHeight(DEFAULT_HEIGHT);
    }, 300);
  };

  useEffect(() => {
    if (isOpen) {
      setLocalFilter(appliedFilterState);

      const initChecked = {};
      appliedFilterState.categoryIds?.forEach((id) => {
        initChecked[id] = true;
      });
      setCheckedCategories(initChecked);

      setSheetHeight(DEFAULT_HEIGHT);
      setCurrentY(0);
    }
  }, [isOpen, appliedFilterState]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isClickInsideSheet = bottomSheetRef.current?.contains(e.target);
      const isClickInsideDropdown =
        e.target.closest('.filter-modal-container') ||
        e.target.closest('.search-filter-button');

      if (!isClickInsideSheet && !isClickInsideDropdown) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    const fetchCategories = async () => {
      const data = await getQnaCategories({ includeAll: true });
      setCategories(data);

      const allCategory = data.find((category) => category.name === 'All');
      if (allCategory) setActiveCategory(allCategory.id);
    };
    fetchCategories();
  }, [setActiveCategory]);

  useEffect(() => {
    const isCategoryChecked = Object.values(checkedCategories).some((v) => v);
    const dirty =
      localFilter.sortBy !== appliedFilterState.sortBy ||
      localFilter.searchIn !== appliedFilterState.searchIn ||
      localFilter.onlyAnswered !== appliedFilterState.onlyAnswered ||
      isCategoryChecked;
    setIsFilterDirty(dirty);
  }, [localFilter, checkedCategories, appliedFilterState]);

  const handleCategoryCheck = (id) => {
    setCheckedCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleApplyClick = async () => {
    const selectedCategoryIds = Object.keys(checkedCategories)
      .filter((key) => checkedCategories[key])
      .map(Number);

    const selectedCategoryNames = categories
      .filter((cat) => selectedCategoryIds.includes(cat.id))
      .map((cat) => cat.name);

    const updatedFilter = {
      ...localFilter,
      categoryNames: selectedCategoryNames,
      categoryIds: selectedCategoryIds,
    };

    setFilterState(updatedFilter);

    const result = await getSearchQna({
      keyword: keyword,
      page: 0,
      sort: localFilter.sortBy,
      searchIn: localFilter.searchIn,
      onlyAnswered: localFilter.onlyAnswered,
      categoryIds: selectedCategoryIds,
    });

    onApply?.({
      filters: updatedFilter,
      category: selectedCategoryIds,
      searchResult: result,
    });

    handleClose();
  };

  const clearFilters = () => {
    const defaultState = {
      sortBy: 'Most Relevant',
      searchIn: 'All',
      onlyAnswered: false,
    };
    setFilterState(defaultState);
    setLocalFilter(defaultState);
    onApply?.(defaultState);
    setActiveCategory(null);
    setCheckedCategories({});
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className='fixed inset-0 z-[10000] flex items-end justify-center'>
      <div
        className='absolute inset-0 bg-surface-black40'
        onClick={handleClose}
      />
      <div
        ref={bottomSheetRef}
        className='relative w-full bg-white rounded-t-[30px] px-[40px] pb-[60px] flex flex-col'
        style={{ height: `${sheetHeight}px` }}
      >
        {/* Handle Bar */}
        <div
          className='py-4 cursor-grab touch-none'
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className='w-[46px] h-[6px] bg-grayscale-gray02 rounded-[20px] mx-auto' />
        </div>

        <div className='flex justify-between items-center pb-6'>
          <span className='typo-title3'>{t('Filters', '필터')}</span>
          <BaseButton
            type='text'
            size='s'
            icon={<CloseIcon />}
            onClick={handleClose}
            bgclass='rounded-full'
          />
        </div>

        <div className='flex-1 overflow-y-auto'>
          {/* Search In */}
          <div className='pb-6'>
            <label className='block typo-b16Strong text-text-primary mb-2'>
              {t('Search in', '검색 범위')}
            </label>
            <SearchInFilterModal
              initialSearchIn={localFilter.searchIn}
              appliedSearchIn={appliedFilterState.searchIn}
              isOpen={isSearchFilterModalOpen}
              setIsOpen={setIsSearchFilterModalOpen}
              onChange={(value) =>
                setLocalFilter((prev) => ({ ...prev, searchIn: value }))
              }
            />
          </div>

          <HorizonLine />

          {/* Only Tutor */}
          <div className='py-6'>
            <Checkbox
              label={t("Only with tutor's answer", '튜터 답변이 있는 질문만')}
              className='w-full flex justify-between flex-row-reverse'
              labelClassName='text-text-primary typo-b14Strong'
              checked={localFilter.onlyAnswered}
              onChange={() =>
                setLocalFilter((prev) => ({
                  ...prev,
                  onlyAnswered: !prev.onlyAnswered,
                }))
              }
            />
          </div>

          <HorizonLine />

          {/* Category List */}
          <div className='py-6 h-auto overflow-y-auto flex-1 scrollbar-none'>
            <label className='block typo-b16Strong pb-2'>
              {t('Category', '카테고리')}
            </label>
            <div>
              {categories.map((category) => {
                const isAllSelected = checkedCategories[1] === true;
                const isThisCategoryAll = category.id === 1;
                const shouldLookDisabled = isAllSelected && !isThisCategoryAll;

                return (
                  <div
                    key={category.id}
                    className={`flex justify-between py-2 ${
                      shouldLookDisabled
                        ? 'text-text-disable'
                        : 'text-text-tertiary'
                    }`}
                  >
                    {category.name}
                    <Checkbox
                      base='placeholder'
                      checked={checkedCategories[category.id] ?? false}
                      onChange={() => handleCategoryCheck(category.id)}
                      disabled={shouldLookDisabled}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className='flex flex-col gap-2 mt-4'>
          <BaseButton
            size='l'
            hideIcon
            isDisabled={!isFilterDirty}
            onClick={handleApplyClick}
          >
            {t('Apply filters', '필터 적용')}
          </BaseButton>
          <BaseButton
            size='l'
            hideIcon
            bgclass='bg-transform border-none'
            textclass='text-text-secondary'
            onClick={clearFilters}
          >
            {t('Clear all', '전체 초기화')}
          </BaseButton>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MobileFilterBottomSheet;
